import type { Permission } from '@/shared/api/types';
import {
  holds,
  INTERFACES,
  SECTION_PERMISSIONS,
  VISITOR_SECTIONS,
  type RoleName,
  type SectionId,
} from '@/shared/lib/roles';
import { isHere, urlOf, type Part } from '@/shared/lib/site';
import type { Device, IconName } from '@/shared/ui/shell';

/** A section of the application, as the bars and the menu list it. */
export interface Section {
  id: SectionId;
  /** The part of the installation its pages are in: maybe another host than this one. */
  part: Part;
  path: string;
  /** Key of its name in the `common` translations. */
  label: string;
  icon: IconName;
}

const SECTIONS: Record<SectionId, Omit<Section, 'id'>> = {
  hub: { part: 'app', path: '/', label: 'nav.hub', icon: 'overview' },
  history: { part: 'app', path: '/history', label: 'nav.history', icon: 'timer' },
  studio: { part: 'app', path: '/studio', label: 'nav.studio', icon: 'dictation' },
  students: { part: 'app', path: '/teaching', label: 'nav.students', icon: 'user' },
  moderation: { part: 'admin', path: '/moderation', label: 'nav.moderation', icon: 'verified' },
  feedback: { part: 'admin', path: '/feedback/inbox', label: 'nav.feedback', icon: 'message' },
  access: { part: 'admin', path: '/access', label: 'nav.access', icon: 'lock' },
  seo: { part: 'admin', path: '/seo', label: 'nav.seo', icon: 'search' },
  glossary: { part: 'app', path: '/glossary', label: 'nav.glossary', icon: 'library' },
  help: { part: 'app', path: '/help', label: 'nav.help', icon: 'help' },
  tools: { part: 'dev', path: '/portal', label: 'nav.tools', icon: 'tune' },
};

/**
 * The sections of a role's interface, most used first (shared/lib/roles.ts says
 * which); without one, a visitor's. Settings are not one of them: they belong to
 * the account, and are reached from its menu.
 */
export function sectionsFor(
  role: RoleName | undefined,
  permissions: readonly Permission[] = [],
): Section[] {
  const ids = role ? INTERFACES[role].sections : VISITOR_SECTIONS;
  // A section some accounts of the role have only with a permission (shared/lib/roles.ts).
  const held = (id: SectionId) => {
    const needed = SECTION_PERMISSIONS[id];
    return needed === undefined || holds(role, permissions, needed);
  };
  return ids.filter(held).map((id) => ({ id, ...SECTIONS[id] }));
}

/**
 * How many sections each device shows by itself: a desktop's bar all of them, a
 * tablet's bar four (the kit's TopBar hides the rest), a phone's tab bar five.
 */
const SHOWN: Record<Device, number> = { desktop: Infinity, tablet: 4, mobile: 5 };

/** The sections a device shows, and those left for the "more" menu. Never the same one twice. */
export function splitSections(sections: readonly Section[], device: Device): { shown: Section[]; more: Section[] } {
  const count = SHOWN[device];
  return { shown: sections.slice(0, count), more: sections.slice(count) };
}

/** Where a section is, from this page: a path, or a whole address on another host. */
export const hrefOf = (section: Section): string => urlOf(section.part, section.path);

const within = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`);

/** Pages that belong to no section: the account's own, and the ways in. */
const OUTSIDE = [
  '/settings',
  '/login',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/join',
  '/feedback',
];
/** Where the sections of every interface are: none of these is a course's address. */
const SECTION_PATHS = Object.values(SECTIONS)
  .map((section) => section.path)
  .filter((path) => path !== '/');

/**
 * The section a page belongs to; none for the pages outside them (settings, signing in).
 *
 * A course or a lesson is at its author's address (`/anna/solfeggio/intervals`), which
 * begins with no fixed word: such a page is part of the hub, which leads to it. So the
 * hub owns every page that is neither another section's nor outside them all.
 */
export function activeSection(sections: readonly Section[], pathname: string): string {
  // A section on another host is never where this page is.
  const here = sections.filter((section) => isHere(section.part));
  const own = here.find((section) => section.path !== '/' && within(pathname, section.path));
  if (own) return own.id;
  const hub = here.find((section) => section.path === '/');
  if (!hub) return '';
  if (pathname === '/') return hub.id;
  // An address has an author and a course at least: one word alone is nobody's page.
  const address = pathname.split('/').filter(Boolean).length >= 2;
  const elsewhere = [...SECTION_PATHS, ...OUTSIDE].some((path) => within(pathname, path));
  return address && !elsewhere ? hub.id : '';
}
